import { memo, type ReactNode } from 'react';

import { ClipType } from '@air/api/types';

import { NoPreviewImage } from '~/components/UI/NoPreviewImage';
import { WorkspaceAgentMentionImageRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionImageRow';
import { WorkspaceAgentAssetTypeIcon } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetTypeIcon';
import { type WorkspaceAgentAssetMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { isWorkspaceAgentAssetClipType } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentAssetClipType';

export type WorkspaceAgentAssetMentionRowProps = {
  /** DOM id so the composer can point `aria-activedescendant` at the highlighted row. */
  id: string;
  index: number;
  /** This row holds the highlight *and* the keyboard is what last moved it, so it takes the focus ring. */
  isKeyboardHighlighted: boolean;
  isSelected: boolean;
  item: WorkspaceAgentAssetMentionItem;
  onHighlight: (index: number) => void;
  onPreviewAnchorElementChange?: (element: HTMLElement | null) => void;
  /** By id, not index: a same-query refetch between paint and click can move this item. */
  onSelect: (itemId: string) => void;
};

export const WorkspaceAgentAssetMentionRow = memo(
  ({
    id,
    index,
    isKeyboardHighlighted,
    isSelected,
    item,
    onHighlight,
    onPreviewAnchorElementChange,
    onSelect,
  }: WorkspaceAgentAssetMentionRowProps) => {
    const glyph: ReactNode = item.ext ? (
      <span className="size-full">
        <NoPreviewImage color="color" ext={item.ext} hideText size="small" />
      </span>
    ) : (
      /* A clip with no extension still needs a tile: an empty one reads as a broken thumbnail. */
      <WorkspaceAgentAssetTypeIcon
        size="xs"
        type={isWorkspaceAgentAssetClipType(item.assetType) ? item.assetType : ClipType.nonMedia}
      />
    );

    return (
      <WorkspaceAgentMentionImageRow
        glyph={glyph}
        id={id}
        index={index}
        isKeyboardHighlighted={isKeyboardHighlighted}
        isSelected={isSelected}
        itemId={item.id}
        onHighlight={onHighlight}
        onPreviewAnchorElementChange={onPreviewAnchorElementChange}
        onSelect={onSelect}
        thumbnailUrl={item.thumbnailUrl}
        title={item.title}
      />
    );
  },
);

WorkspaceAgentAssetMentionRow.displayName = 'WorkspaceAgentAssetMentionRow';
